import { createContext, useContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { media } from "../../../styles/media";

const EXIT_DURATION = 320;

const SideModalCloseContext = createContext(null);

export const useSideModalClose = () => useContext(SideModalCloseContext);

const SideModalStyles = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: stretch;
  justify-content: flex-end;
  position: fixed;
  background: ${({ $isExiting }) => ($isExiting ? "transparent" : "#00000040")};
  z-index: 1000;
  top: 0;
  left: 0;
  padding: 16px;
  transition: background ${EXIT_DURATION}ms ease-in-out;

  .modalContainer {
    width: 552px;
    max-width: 100%;
    height: 100%;
    overflow: hidden;
    border-radius: ${({ $isChatOpen }) =>
      $isChatOpen ? "0 16px 16px 0" : "16px"};
    transition: border-radius 0.35s cubic-bezier(0.32, 0.72, 0.32, 1);
    animation: ${({ $isExiting }) =>
        $isExiting ? "hideModal" : "displayModal"}
      ${EXIT_DURATION}ms ease-in-out forwards;

    ${media.maxMobile`
      width: 100%;
    `}
  }

  /* The visible dialog surface is the .tiketDetailsCard inside the modal;
     the modalContainer is transparent and only acts as a clip mask. Without
     this override its own 16px radius shows through at the seam. The
     transition runs in parallel with the chat slide so both feel like one
     coordinated motion. */
  .modalContainer .tiketDetailsCard {
    border-radius: ${({ $isChatOpen }) =>
      $isChatOpen ? "0 16px 16px 0" : "16px"};
    transition: border-radius 0.35s cubic-bezier(0.32, 0.72, 0.32, 1);
  }

  @keyframes displayModal {
    from {
      transform: translateX(100px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @keyframes hideModal {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(100px);
      opacity: 0;
    }
  }
`;

const SideModal = ({ children, closeModal, isChatOpen = false }) => {
  const [isExiting, setIsExiting] = useState(false);
  const timerRef = useRef(null);

  const startExit = () => {
    if (isExiting) return;
    setIsExiting(true);
    timerRef.current = setTimeout(() => {
      closeModal && closeModal();
    }, EXIT_DURATION);
  };

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  return (
    <SideModalCloseContext.Provider value={startExit}>
      <SideModalStyles
        onClick={startExit}
        $isExiting={isExiting}
        $isChatOpen={isChatOpen}
      >
        <div className="modalContainer" onClick={e => e.stopPropagation()}>
          {children}
        </div>
      </SideModalStyles>
    </SideModalCloseContext.Provider>
  );
};

export default SideModal;
